import React from 'react';
import type { PushSendMode } from '../../services/resortAdminApi';
import {
  isInsidePushQuietHours,
  PUSH_QUIET_HOURS_END,
  PUSH_QUIET_HOURS_NOTICE,
  PUSH_TIMEZONE_NOTICE,
} from '../../utils/pushCampaignValidation';

const SEND_MODE_OPTIONS: { value: PushSendMode; label: string; description: string; }[] = [
  { value: 'now', label: 'Send now', description: 'Sending starts as soon as the campaign is launched.' },
  { value: 'scheduled', label: 'Schedule', description: 'Sending starts at the chosen date and time.' },
];

interface ScheduleStepProps {
  sendMode: PushSendMode;
  scheduledAt: string;
  errors: string[];
  onSendModeChange: (value: PushSendMode) => void;
  onScheduledAtChange: (value: string) => void;
}

export const ScheduleStep: React.FC<ScheduleStepProps> = props => {
  return (
    <div className="space-y-4">
      <span className="block text-sm font-medium text-gray-700">When to send</span>
      <div className="space-y-2">
        {SEND_MODE_OPTIONS.map(x => (
          <label
            key={x.value}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
              props.sendMode === x.value ? 'border-alps-primary bg-alps-primary/5' : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              name="push-send-mode"
              value={x.value}
              checked={props.sendMode === x.value}
              onChange={() => props.onSendModeChange(x.value)}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">{x.label}</span>
              <span className="block text-xs text-gray-500">{x.description}</span>
            </span>
          </label>
        ))}
      </div>

      {props.sendMode === 'scheduled' && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Start at</label>
          <input
            type="datetime-local"
            value={props.scheduledAt}
            onChange={x => props.onScheduledAtChange(x.target.value)}
            className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none"
          />
          <p className="mt-1 text-xs text-gray-500">{PUSH_TIMEZONE_NOTICE}</p>
        </div>
      )}

      <p className="text-xs text-gray-500">{PUSH_QUIET_HOURS_NOTICE}</p>

      {isInsidePushQuietHours(props.sendMode === 'scheduled' ? props.scheduledAt : null) && (
        <p className="border-l-4 border-yellow-500 bg-yellow-50 p-3 text-sm text-yellow-800">
          The chosen time falls inside the quiet hours. The campaign will wait and start at {PUSH_QUIET_HOURS_END}.
        </p>
      )}

      {props.errors.length > 0 && (
        <ul className="space-y-1 border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-700">
          {props.errors.map(x => <li key={x}>{x}</li>)}
        </ul>
      )}
    </div>
  );
};
